import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { useNoshStore } from './lib/store';
import { initializePWASession, setupPWAHandlers } from './lib/pwa-session';
import { useToast } from './components/ui/use-toast';
import { databaseService } from './lib/database';

// Page components
import LandingPage from './pages/LandingPage';
import AuthLayout from './components/layout/AuthLayout';
import Login from './pages/auth/Login';
import SignUp from './pages/auth/SignUp';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import Recipes from './pages/Recipes';
import WeeklyPlanner from './pages/WeeklyPlanner';
import MealTracker from './pages/MealTracker';
import ShoppingList from './pages/ShoppingList';
import Profile from './pages/Profile';
import NutritionAnalysis from './pages/NutritionAnalysis';
import HelpCenter from './pages/HelpCenter';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import { OnboardingModal } from './components/onboarding/OnboardingModal';
import { NotificationManager } from './components/NotificationManager';
import { UsageLimitModal } from './components/UsageLimitModal';

// Session validation utility
const validateSession = async (): Promise<boolean> => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.warn('Session validation error:', error);
      return false;
    }
    
    return !!session;
  } catch (error) {
    console.warn('Error validating session:', error);
    return false;
  }
};

function App() {
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [sessionValidated, setSessionValidated] = useState(false);
  const { toast } = useToast();
  const { loadAllData, onboarding, hideOnboarding, setOnboardingComplete, checkFirstTimeUser, usageLimitModal, hideUsageLimitModal } = useNoshStore();
  const location = useLocation();

  // Define public routes that don't need session validation
  const publicRoutes = [
    '/',                // Landing page
    '/auth', 
    '/auth/login', 
    '/auth/signup',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/email-verified',
    '/help-center',
    '/contact',
    '/privacy-policy',
    '/terms-of-service'
  ];

  const isPublicRoute = publicRoutes.includes(location.pathname) || 
                       location.pathname.startsWith('/auth');

  // Session validation for authenticated routes
  useEffect(() => {
    const performSessionValidation = async () => {
      // Skip validation for public routes
      if (isPublicRoute) {
        setSessionValidated(true);
        return;
      }

      // Only validate after app has initialized to avoid premature redirects
      if (!hasInitialized) {
        return;
      }

      // Only validate once per session
      if (sessionValidated) {
        return;
      }

      console.log('🔍 Validating session for authenticated route...');
      
      try {
        const hasValidSession = await validateSession();
        
        if (!hasValidSession) {
          console.warn('❌ No valid session found - redirecting to login');
          
          // Import logout utility and redirect
          const { getLogoutRedirectUrl } = await import('./lib/logout-utils');
          window.location.href = getLogoutRedirectUrl();
          return;
        }
        
        console.log('✅ Session validated successfully');
        setSessionValidated(true);
      } catch (error) {
        console.error('Session validation failed:', error);
        
        // On validation error, redirect to login as a safety measure
        const { getLogoutRedirectUrl } = await import('./lib/logout-utils');
        window.location.href = getLogoutRedirectUrl();
      }
    };

    performSessionValidation();
  }, [hasInitialized, isPublicRoute, sessionValidated, location.pathname]);

  // Initialize app data only once when first accessing authenticated routes
  useEffect(() => {
    const initializeAppForAuthenticatedRoutes = async () => {
      // Skip initialization for public routes and landing page
      if (isPublicRoute) {
        console.log('Public route detected, skipping app initialization');
        setIsLoading(false);
        return;
      }

      // Only initialize once
      if (hasInitialized) {
        // App already initialized
        return;
      }

      // Only load data for authenticated routes - but only once
      try {
        setIsLoading(true);
        console.log('Authenticated route detected, initializing app...');
        
        // First ensure user has all required records before loading app data
        try {
          console.log('🔍 Checking if user has required database records...');
          const result = await databaseService.ensureUserPlan();
          if (result.success) {
            console.log('✅ User records verified/created successfully');
          } else {
            console.warn('⚠️ Failed to ensure user records:', result.message);
          }
        } catch (error) {
          console.warn('⚠️ Error ensuring user records during app init:', error);
        }
        
        await loadAllData();
        setHasInitialized(true);
        console.log('App initialization complete');
      } catch (error) {
        console.error('Error initializing app:', error);
        toast({
          title: 'Initialization Error',
          description: 'There was an issue loading the app. Please refresh the page.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    initializeAppForAuthenticatedRoutes();
  }, [location.pathname, hasInitialized]); // Check route changes but prevent re-initialization

  // Initialize PWA session management
  useEffect(() => {
    const initializePWA = async () => {
      await initializePWASession();
      setupPWAHandlers();
    };
    
    initializePWA();
  }, []);

  // Check onboarding state only once after initial app initialization
  useEffect(() => {
    // Only check after the app has been initialized and we're not on a public route
    // Don't re-check on navigation - only on initial load
    
    // Only run this check once when the app first initializes, not on every route change
    if (!isPublicRoute && hasInitialized && !onboarding.hasCompletedOnboarding) {
      console.log('🔄 Checking onboarding after app initialization');
      // Small delay to ensure store is fully rehydrated
      setTimeout(() => {
        checkFirstTimeUser();
      }, 500);
    }
  }, [hasInitialized]); // Removed location.pathname and checkFirstTimeUser dependencies

  // Handle authentication state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_OUT') {
          console.log('User signed out, resetting state...');
          useNoshStore.getState().resetUserProfile();
          // Clear PWA session data on logout
          const { PWASessionManager } = await import('./lib/pwa-session');
          PWASessionManager.clearSessionData();
          // Reset session validation state
          setSessionValidated(false);
        } else if (event === 'SIGNED_IN' && session) {
          // Only process SIGNED_IN for actual new sign-ins, not tab-switching session restoration
          // Use a more reliable check: if user is already on an authenticated route, skip user plan check
          const currentPath = window.location.pathname;
          const isOnAuthenticatedRoute = !['/login', '/signup', '/forgot-password', '/reset-password', '/'].includes(currentPath);
          
          if (!isOnAuthenticatedRoute) {
            // Genuine new sign-in from login/signup pages
            console.log('🔑 User signed in via OAuth, session detected');
            console.log('🔑 Session user ID:', session.user.id);
            console.log('🔑 Session user email:', session.user.email);
            
            // Ensure user has a plan in user_plans table (fixes OAuth signup issue)
            try {
              console.log('🚀 Calling ensureUserPlan with session data...');
              const result = await databaseService.ensureUserPlanWithSession(session.user.id, session.user.email || '');
              
              if (result.success) {
                console.log('✅ User plan ensured successfully:', result.message);
              } else {
                console.error('❌ Failed to ensure user plan:', result.message);
              }
            } catch (error) {
              console.error('💥 Exception ensuring user plan:', error);
            }
            
            // Reset session validation state to trigger re-validation
            setSessionValidated(false);
            // This will be handled by the LandingPage authentication check
            // which will redirect to dashboard if user is authenticated
          } else {
            // Tab switching session restoration on authenticated route - skip user plan check
            console.log('🔄 Session restored on authenticated route (skipping user plan check)');
          }
        }
        // Ignore TOKEN_REFRESHED and other events that fire during tab switching
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Show loading spinner only for authenticated routes during data loading
  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center flex-col">
        <img 
          src="/robo_run.webp" 
          alt="Loading robot" 
          className="mb-4"
        />
        <p className="text-lg text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const handleOnboardingClose = () => {
    hideOnboarding();
  };

  return (
    <>
      <Routes>
        {/* Landing Page - Root Route */}
        <Route index element={<LandingPage />} />
        
        {/* Public Routes */}
        <Route path="/help-center" element={<HelpCenter />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        
        {/* Authentication Routes */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route path="login" element={<Login />} />
          <Route path="signup" element={<SignUp />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="email-verified" element={<Login />} />
        </Route>
        
        {/* Protected App Routes */}
        <Route path="/*" element={<Layout />}>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="recipes" element={<Recipes />} />
          <Route path="planner" element={<WeeklyPlanner />} />
          <Route path="tracker" element={<MealTracker />} />
          <Route path="shopping" element={<ShoppingList />} />
          <Route path="profile" element={<Profile />} />
          <Route path="nutrition-analysis" element={<NutritionAnalysis />} />
        </Route>
      </Routes>

      {/* Onboarding Modal - Only show on authenticated/app routes, never on public routes */}
      {!isPublicRoute && (
        <OnboardingModal
          isOpen={onboarding.showOnboarding}
          onClose={handleOnboardingClose}
        />
      )}

      {/* Notification Manager - handles background notification checks */}
      <NotificationManager />

      {/* Global Usage Limit Modal - shows from anywhere in the app */}
      <UsageLimitModal
        isOpen={usageLimitModal.isOpen}
        onClose={hideUsageLimitModal}
        limits={usageLimitModal.limits}
        featureType={usageLimitModal.featureType}
      />
    </>
  );
}

export default App;